import { useContext } from 'react'

import { ToastContext } from '@reveni-io/reveni-ui'
import { Divider } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'

import APICredentials from './APICredentials/APICredentials'
import ContactInformation from './ContactInformation/ContactInformation'
import useStoreInfoConfiguration from './hooks/useStoreInfoConfiguration'
import InformationForCustomersConfiguration from './InformationForCustomersConfiguration/InformationForCustomersConfiguration'
import StoreConfigurationSkeleton from './StoreConfigurationSkeleton/StoreConfigurationSkeleton'
import StoreInfo from './StoreInfo/StoreInfo'
import SupportedLanguages from './SupportedLanguages/SupportedLanguages'
import * as Styles from './styles'

const StoreConfiguration = () => {
  const { info, updateContext } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const {
    loading,
    supportedLanguages,
    onChangeContactEmail,
    contactEmail,
    onChangeSupportedLanguages,
    defaultLanguage,
    isEmailValid,
    setDefaultLanguage,
    hasChanges,
    resetFields,
    returnsPolicyUrl,
    setReturnsPolicyUrl,
    helpCenterUrl,
    setHelpCenterUrl,
    onSave,
    contactUrl,
    onChangeContactUrl,
  } = useStoreInfoConfiguration({
    setToastInfo,
    selectedStore: info?.selectedStore,
    updateContext,
    returnPortalId: info?.selectedReturnPortal?.id,
  })
  const { hasPermission } = useUserPermissions()

  if (loading) {
    return <StoreConfigurationSkeleton />
  }

  return (
    <Styles.MainContainer>
      {hasChanges && <UnsavedChangesAlert hasPermission={hasPermission(userPermissions.editStore)} />}
      <StoreInfo info={info} />
      <ContactInformation
        contactEmail={contactEmail}
        onChangeContactEmail={onChangeContactEmail}
        contactUrl={contactUrl}
        onChangeContactUrl={onChangeContactUrl}
      />
      <Divider />
      <SupportedLanguages
        defaultLanguage={defaultLanguage}
        handleChangeDefaultLanguage={setDefaultLanguage}
        supportedLanguages={supportedLanguages}
        handleChangeSupportedLanguage={onChangeSupportedLanguages}
      />
      <Divider />
      <InformationForCustomersConfiguration
        returnsPolicyUrl={returnsPolicyUrl}
        onChangeReturnsPolicyUrl={setReturnsPolicyUrl}
        helpCenterUrl={helpCenterUrl}
        onChangeHelpCenterUrl={setHelpCenterUrl}
      />
      <Divider />
      <APICredentials info={info} />

      <SaveChangesButtons
        hasChanges={hasPermission(userPermissions.editStore) && hasChanges}
        onSave={onSave}
        onRevertChanges={resetFields}
        loading={loading}
        disabled={loading || !isEmailValid || supportedLanguages?.length === 0 || !defaultLanguage}
      />
    </Styles.MainContainer>
  )
}

export default StoreConfiguration
